// IdleTimeoutGuard — operator-side idle-timeout enforcer (plan madde 2.8).
//
// Wraps children. While the operator is authenticated, runs useIdleTimeout
// against the configured idle window (default 1800s = 30min, mirroring
// the operators-realm `ssoSessionIdleTimeout`). On idle, calls
// auth.signoutRedirect() — the OIDC end-session flow lands the operator
// back on the home route's "Sign in" button.
//
// Override via VITE_OPERATOR_SESSION_IDLE_TIMEOUT_SEC at build time
// (spec 60 frontend scope). The override can only shorten the Keycloak
// realm's 1800-second ceiling; it must never extend an unattended operator
// session beyond the server-side policy.
//
// Composes inside the AuthProvider so useAuth() is in scope; sits inside
// the QueryClient/i18n providers so a forced sign-out can still hydrate
// translated farewell screens later (plan madde 11 warning toast).

import type { ReactNode } from "react";
import { useAuth } from "react-oidc-context";

import { useIdleTimeout } from "@/hooks/useIdleTimeout";

export const OPERATOR_REALM_IDLE_TIMEOUT_SEC = 1800;

/** Resolve a public build-time UX timeout without ever exceeding the Keycloak realm policy. */
export function resolveOperatorIdleTimeoutSec(raw: unknown): number {
  if (typeof raw !== "string" || raw.length === 0) return OPERATOR_REALM_IDLE_TIMEOUT_SEC;
  const parsed = Number.parseInt(raw, 10);
  // Defensive — ignore non-numeric / non-positive values rather than
  // accidentally disabling the timeout. An explicit shorter value is a
  // conservative UX policy; a larger value is capped by the realm policy.
  return Number.isFinite(parsed) && parsed > 0
    ? Math.min(parsed, OPERATOR_REALM_IDLE_TIMEOUT_SEC)
    : OPERATOR_REALM_IDLE_TIMEOUT_SEC;
}

export function IdleTimeoutGuard({ children }: { children: ReactNode }): ReactNode {
  const auth = useAuth();
  const idleSec = resolveOperatorIdleTimeoutSec(
    import.meta.env["VITE_OPERATOR_SESSION_IDLE_TIMEOUT_SEC"],
  );

  useIdleTimeout({
    idleSec,
    enabled: auth.isAuthenticated,
    onIdle: (): void => {
      // signoutRedirect is async but useIdleTimeout's onIdle is sync —
      // fire-and-forget is fine: any error during the OIDC end-session
      // flow surfaces in the browser console + Sentry / GlitchTip
      // (plan madde 12.2). The user's session is already considered
      // expired at this point regardless of the redirect outcome.
      void auth.signoutRedirect();
    },
  });

  return children;
}
